import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clinicHighlights } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import { useAssets } from '../assets/AssetsContext';

const ClinicHighlights = () => {
  const { t } = useLang();
  const { overrides, apiBase } = useAssets();
  const [active, setActive] = useState(clinicHighlights[0].id);
  const [idx, setIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const current = clinicHighlights.find((c) => c.id === active);
  const images = current.images.map((src, i) => {
    const ov = overrides[`clinic.${current.id}.${i}`];
    return ov ? `${apiBase}${ov.url}` : src;
  });

  React.useEffect(() => {
    const m = window.matchMedia('(max-width: 767px)');
    const apply = () => setIsMobile(m.matches);
    apply();
    m.addEventListener('change', apply);
    return () => m.removeEventListener('change', apply);
  }, []);

  const next = () => setIdx((i) => (i + 1) % images.length);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);

  const switchTab = (id) => {
    setActive(id);
    setIdx(0);
  };

  const getVisible = () => {
    const result = [];
    for (let i = -1; i <= 2; i++) {
      result.push({ img: images[(idx + i + images.length) % images.length], pos: i });
    }
    return result;
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#f5ede2] text-[#0B3D2E]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 text-center">
        <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl mb-4 leading-tight">
          <span className="text-[#0B3D2E]">{t('ch.title')} </span>
          <span className="peach-text italic">{t('ch.title.italic')}</span>
        </h2>
        <p className="text-[#0B3D2E]/65 text-sm sm:text-base mb-8 sm:mb-10 px-2">
          {t('ch.sub')}
        </p>

        <div className="mb-10 sm:mb-14 overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0 scrollbar-hide">
          <div className="inline-flex items-center gap-1 bg-white border border-[#D4AF37]/30 rounded-full p-1.5 whitespace-nowrap">
            {clinicHighlights.map((tab) => (
              <button
                key={tab.id}
                onClick={() => switchTab(tab.id)}
                data-testid={`ch-tab-${tab.id}`}
                className={`px-5 sm:px-6 py-2.5 rounded-full text-sm transition-all ${
                  active === tab.id
                    ? 'bg-[#0B3D2E] text-[#ffffff] font-semibold'
                    : 'text-[#0B3D2E]/70 hover:text-[#0B3D2E]'
                }`}
              >
                {t(`ch.tab.${tab.id}`, tab.label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6">
        {isMobile ? (
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl aspect-[4/5] max-w-[420px] mx-auto">
              <img
                src={images[idx]}
                alt={`${current.label} photo`}
                className="w-full h-full object-cover"
                loading="lazy"
                key={images[idx]}
              />
            </div>
            <div className="mt-5 flex items-center justify-center gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? 'w-8 bg-[#0B3D2E]' : 'w-2 bg-[#0B3D2E]/30'
                  }`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-5 min-h-[560px]">
            {getVisible().map((item, i) => (
              <div
                key={i}
                className={`rounded-3xl overflow-hidden transition-all duration-700 shadow-xl ${
                  item.pos === 0 || item.pos === 1
                    ? 'w-[32%] aspect-[4/5] opacity-100'
                    : 'w-[16%] aspect-[3/5] opacity-60'
                }`}
              >
                <img
                  src={item.img}
                  alt={`${current.label} photo`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        <button
          onClick={prev}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white text-[#0B3D2E] flex items-center justify-center border border-[#D4AF37]/30 hover:bg-[#0B3D2E] hover:text-[#ffffff] transition-colors shadow-lg z-10"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={next}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#0B3D2E] text-[#ffffff] flex items-center justify-center hover:bg-[#175C45] transition-colors shadow-lg z-10"
          aria-label="Next slide"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
};

export default ClinicHighlights;
